import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeftIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  BriefcaseIcon,
  CheckCircleIcon,
  CalendarIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import { rsvpService } from "../../services/rsvpService";
import { useAuth } from "../../context/AuthContext";
import LoadingSpinner from "../../components/LoadingSpinner";

const RSVPDetailPage: React.FC = () => {
  const { rsvpId } = useParams<{ rsvpId: string }>();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Fetch RSVP details
  const {
    data: rsvp,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["rsvp-detail", rsvpId],
    queryFn: () => rsvpService.getRSVPById(rsvpId!),
    enabled: !!rsvpId,
  });

  const handleBack = () => {
    navigate("/admin/dashboard");
  };

  if (isLoading) {
    return <LoadingSpinner fullScreen text="Loading RSVP details..." />;
  }

  if (error || !rsvp) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            RSVP Not Found
          </h1>
          <p className="text-gray-600 mb-6">
            The RSVP you're looking for doesn't exist.
          </p>
          <button onClick={handleBack} className="btn-primary">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 admin-dashboard">
      {/* Header */}
      <motion.header
        className="bg-white border-b border-gray-200"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBack}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5" />
                <span className="text-sm font-medium">Back to Dashboard</span>
              </button>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-gray-900">
                  {user?.email}
                </p>
                <p className="text-xs text-gray-500">Admin</p>
              </div>
              <button
                onClick={logout}
                className="btn-secondary text-xs sm:text-sm px-3 sm:px-4 py-2"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          className="card p-6 sm:p-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          {/* RSVP Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                RSVP Details
              </h1>
              <p className="text-gray-600">
                Complete information for {rsvp.fullName}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <div
                className={`w-3 h-3 rounded-full ${
                  rsvp.attendance === "attending"
                    ? "bg-success-500"
                    : "bg-danger-500"
                }`}
              />
              <span
                className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                  rsvp.attendance === "attending"
                    ? "bg-success-100 text-success-800"
                    : "bg-danger-100 text-danger-800"
                }`}
              >
                {rsvp.attendance === "attending"
                  ? "Attending"
                  : "Not Attending"}
              </span>
            </div>
          </div>

          {/* RSVP Information Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Personal Information */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <UserIcon className="w-5 h-5 mr-2 text-primary-600" />
                Personal Information
              </h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <UserIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Full Name
                    </p>
                    <p className="text-gray-900">{rsvp.fullName}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <EnvelopeIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Email</p>
                    <p className="text-gray-900">{rsvp.email}</p>
                  </div>
                </div>

                {rsvp.phone && (
                  <div className="flex items-start space-x-3">
                    <PhoneIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">Phone</p>
                      <p className="text-gray-900">{rsvp.phone}</p>
                    </div>
                  </div>
                )}

                {rsvp.company && (
                  <div className="flex items-start space-x-3">
                    <BuildingOfficeIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Company
                      </p>
                      <p className="text-gray-900">{rsvp.company}</p>
                    </div>
                  </div>
                )}

                {rsvp.role && (
                  <div className="flex items-start space-x-3">
                    <BriefcaseIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">Role</p>
                      <p className="text-gray-900">{rsvp.role}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Event & Additional Information */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <CalendarIcon className="w-5 h-5 mr-2 text-primary-600" />
                Event & Additional Information
              </h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Attendance Status
                    </p>
                    <p className="text-gray-900 capitalize">
                      {rsvp.attendance.replace("-", " ")}
                    </p>
                  </div>
                </div>

                {rsvp.plusOne && (
                  <div className="flex items-start space-x-3">
                    <UserIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Plus One
                      </p>
                      <p className="text-gray-900">
                        {rsvp.plusOneName || "Yes, but name not provided"}
                      </p>
                    </div>
                  </div>
                )}

                {rsvp.specialRequests && (
                  <div className="flex items-start space-x-3">
                    <div className="w-5 h-5 text-gray-400 mt-0.5 flex items-center justify-center">
                      <span className="text-xs">📝</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Questions/Comments
                      </p>
                      <p className="text-gray-900">{rsvp.specialRequests}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-start space-x-3">
                  <ClockIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Submitted At
                    </p>
                    <p className="text-gray-900">
                      {new Date(rsvp.submittedAt).toLocaleString()}
                    </p>
                  </div>
                </div>

                {rsvp.updatedAt !== rsvp.submittedAt && (
                  <div className="flex items-start space-x-3">
                    <ClockIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Last Updated
                      </p>
                      <p className="text-gray-900">
                        {new Date(rsvp.updatedAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-8 pt-6 border-t border-gray-200 flex flex-col sm:flex-row gap-4">
            <button
              onClick={handleBack}
              className="btn-secondary flex-1 sm:flex-none"
            >
              Back to Dashboard
            </button>
            <button
              onClick={() => window.print()}
              className="btn-primary flex-1 sm:flex-none"
            >
              Print RSVP
            </button>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default RSVPDetailPage;
